(() => {
  'use strict';

  if (window.__numeoCookieYesShopifyConsentBridge) return;
  window.__numeoCookieYesShopifyConsentBridge = true;

  const COOKIEYES_BANNER_LOAD_EVENT = 'cookieyes_banner_load';
  const COOKIEYES_CONSENT_UPDATE_EVENT = 'cookieyes_consent_update';
  const SHOPIFY_PRIVACY_LOADED_EVENT = 'shopifyCustomerPrivacyApiLoaded';
  const SHOPIFY_API_TIMEOUT_MS = 15000;
  const SDK_CHECK_INTERVAL_MS = 100;
  const SDK_CHECK_LIMIT = 150;

  let customerPrivacyPromise;
  let queuedConsent;
  let isSubmitting = false;

  function debug(...args) {
    try {
      if (window.localStorage.getItem('numeoConsentDebug') === '1') {
        console.info('[Numeo consent bridge]', ...args);
      }
    } catch (_error) {
      // localStorage can be unavailable in privacy-restricted browser modes.
    }
  }

  function getCookieYesConsent() {
    if (typeof window.getCkyConsent !== 'function') return undefined;

    try {
      return window.getCkyConsent();
    } catch (error) {
      debug('CookieYes consent could not be read.', error);
      return undefined;
    }
  }

  function categoryValue(category, categories, accepted, rejected) {
    if (Object.prototype.hasOwnProperty.call(categories, category)) {
      return categories[category] === true;
    }

    if (accepted.includes(category)) return true;
    if (rejected.includes(category)) return false;

    return undefined;
  }

  function consentFromCookieYesEvent(event) {
    const detail = event?.detail || {};
    const cookieYesConsent = getCookieYesConsent();
    const isConsentUpdate = event?.type === COOKIEYES_CONSENT_UPDATE_EVENT;
    const hasStoredDecision =
      cookieYesConsent?.isUserActionCompleted === true ||
      detail.isUserActionCompleted === true;

    // Never create a Shopify decision for a first-time visitor who has not
    // interacted with CookieYes.
    if (!isConsentUpdate && !hasStoredDecision) return undefined;

    const accepted = Array.isArray(detail.accepted) ? detail.accepted : [];
    const rejected = Array.isArray(detail.rejected) ? detail.rejected : [];
    const updateHasCategoryLists = accepted.length > 0 || rejected.length > 0;
    const categories =
      (isConsentUpdate && updateHasCategoryLists
        ? detail.categories
        : cookieYesConsent?.categories || detail.categories) || {};

    let analytics = categoryValue('analytics', categories, accepted, rejected);

    // Some CookieYes configurations use "performance" instead of
    // "analytics". Only use it as a fallback; an explicit analytics denial
    // must never be overridden by an accepted performance category.
    if (analytics === undefined) {
      analytics = categoryValue('performance', categories, accepted, rejected);
    }

    const marketing = categoryValue(
      'advertisement',
      categories,
      accepted,
      rejected,
    );
    const preferences = categoryValue(
      'functional',
      categories,
      accepted,
      rejected,
    );
    const consent = {};

    if (analytics !== undefined) consent.analytics = analytics;
    if (marketing !== undefined) consent.marketing = marketing;
    if (preferences !== undefined) consent.preferences = preferences;

    return Object.keys(consent).length > 0 ? consent : undefined;
  }

  function customerPrivacyReady() {
    return (
      typeof window.Shopify?.customerPrivacy?.setTrackingConsent === 'function'
    );
  }

  function waitForCustomerPrivacy() {
    if (customerPrivacyReady()) {
      return Promise.resolve(window.Shopify.customerPrivacy);
    }

    if (customerPrivacyPromise) return customerPrivacyPromise;

    customerPrivacyPromise = new Promise((resolve, reject) => {
      const startedAt = Date.now();
      let loadRequested = false;
      let settled = false;

      const finish = () => {
        if (settled || !customerPrivacyReady()) return false;
        settled = true;
        document.removeEventListener(SHOPIFY_PRIVACY_LOADED_EVENT, finish);
        resolve(window.Shopify.customerPrivacy);
        return true;
      };

      const requestLoad = () => {
        if (loadRequested || typeof window.Shopify?.loadFeatures !== 'function') {
          return;
        }

        loadRequested = true;
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            if (error) {
              debug('Shopify Customer Privacy API load reported an error.', error);
            }
            finish();
          },
        );
      };

      const check = () => {
        if (settled || finish()) return;
        requestLoad();

        if (Date.now() - startedAt >= SHOPIFY_API_TIMEOUT_MS) {
          settled = true;
          document.removeEventListener(SHOPIFY_PRIVACY_LOADED_EVENT, finish);
          customerPrivacyPromise = undefined;
          reject(new Error('Shopify Customer Privacy API did not become ready.'));
          return;
        }

        window.setTimeout(check, SDK_CHECK_INTERVAL_MS);
      };

      document.addEventListener(SHOPIFY_PRIVACY_LOADED_EVENT, finish);
      requestLoad();
      check();
    });

    return customerPrivacyPromise;
  }

  function currentConsentMatches(customerPrivacy, consent) {
    if (typeof customerPrivacy.currentVisitorConsent !== 'function') return false;

    const current = customerPrivacy.currentVisitorConsent();

    return Object.entries(consent).every(
      ([purpose, allowed]) => current?.[purpose] === (allowed ? 'yes' : 'no'),
    );
  }

  function finishSubmission() {
    isSubmitting = false;
    if (queuedConsent) submitQueuedConsent();
  }

  function submitQueuedConsent() {
    if (isSubmitting || !queuedConsent) return;

    const consent = queuedConsent;
    queuedConsent = undefined;
    isSubmitting = true;

    waitForCustomerPrivacy()
      .then((customerPrivacy) => {
        if (currentConsentMatches(customerPrivacy, consent)) {
          debug('Shopify already has the current CookieYes decision.', consent);
          finishSubmission();
          return;
        }

        customerPrivacy.setTrackingConsent(consent, (result) => {
          if (result?.error) {
            console.error(
              '[Numeo consent bridge] Shopify rejected the CookieYes decision.',
              result.error,
            );
          } else {
            debug('CookieYes decision synced to Shopify.', consent);
            document.dispatchEvent(
              new CustomEvent('numeo:shopify-consent-synced', {
                detail: { source: 'cookieyes' },
              }),
            );
          }

          finishSubmission();
        });
      })
      .catch((error) => {
        // Keep only the newest decision so that a later API-ready event or
        // CookieYes update can retry without applying stale consent.
        if (!queuedConsent) queuedConsent = consent;
        debug(error.message);
        isSubmitting = false;
      });
  }

  function queueConsent(consent) {
    if (!consent) return;
    queuedConsent = consent;
    submitQueuedConsent();
  }

  function handleCookieYesEvent(event) {
    queueConsent(consentFromCookieYesEvent(event));
  }

  document.addEventListener(
    COOKIEYES_BANNER_LOAD_EVENT,
    handleCookieYesEvent,
  );
  document.addEventListener(
    COOKIEYES_CONSENT_UPDATE_EVENT,
    handleCookieYesEvent,
  );
  document.addEventListener(SHOPIFY_PRIVACY_LOADED_EVENT, submitQueuedConsent);

  // Covers the case where CookieYes loaded before this asset and the native
  // banner-load event was already dispatched.
  let sdkChecks = 0;
  const syncExistingDecision = () => {
    const cookieYesConsent = getCookieYesConsent();

    if (cookieYesConsent) {
      queueConsent(
        consentFromCookieYesEvent({
          type: COOKIEYES_BANNER_LOAD_EVENT,
          detail: cookieYesConsent,
        }),
      );
      return;
    }

    sdkChecks += 1;
    if (sdkChecks < SDK_CHECK_LIMIT) {
      window.setTimeout(syncExistingDecision, SDK_CHECK_INTERVAL_MS);
    }
  };

  syncExistingDecision();
})();
